/**
 * Phone app appearance. Default follows the device; a tap stores Light or Dark.
 */

import { useEffect, useState } from 'react';

export const THEME_STORAGE_KEY = 'lgl-m-theme';
export const TYPE_SIZE_KEY = 'lgl-m-type-size';
export const FONT_KEY = 'lgl-m-font';

export const THEME_OPTIONS = [
  { id: 'light', label: 'Light' },
  { id: 'cream', label: 'Cream' },
  { id: 'dark', label: 'Dark' },
  { id: 'system', label: 'Auto' },
];

export const TYPE_SIZE_OPTIONS = [
  { id: 'small', label: 'Small' },
  { id: 'medium', label: 'Medium' },
  { id: 'large', label: 'Large' },
];

const TYPE_SIZE_IDS = TYPE_SIZE_OPTIONS.map((option) => option.id);

export const FONT_OPTIONS = [
  { id: 'system', label: 'System' },
  { id: 'serif', label: 'Serif' },
  { id: 'wide', label: 'Wide' },
];

export const readThemePreference = (win = typeof window === 'undefined' ? undefined : window) => {
  try {
    const value = win?.localStorage?.getItem(THEME_STORAGE_KEY);
    return value === 'light' || value === 'dark' || value === 'cream' ? value : 'system';
  } catch {
    return 'system';
  }
};

export const writeThemePreference = (value, win = typeof window === 'undefined' ? undefined : window) => {
  try {
    if (!win?.localStorage) return;
    if (value === 'light' || value === 'dark' || value === 'cream') {
      win.localStorage.setItem(THEME_STORAGE_KEY, value);
    } else {
      win.localStorage.removeItem(THEME_STORAGE_KEY);
    }
  } catch {
    /* private mode */
  }
};

export const systemPrefersDark = (win = typeof window === 'undefined' ? undefined : window) => {
  try {
    return Boolean(win?.matchMedia?.('(prefers-color-scheme: dark)')?.matches);
  } catch {
    return false;
  }
};

export const resolveTheme = (preference, win) => {
  if (preference === 'light' || preference === 'dark' || preference === 'cream') return preference;
  return systemPrefersDark(win) ? 'dark' : 'light';
};

export const oppositeTheme = (theme) => (theme === 'dark' ? 'light' : 'dark');

const pickStored = (value, allowed, fallback) => (
  allowed.includes(value) ? value : fallback
);

export const normalizeTypeSize = (value) => {
  if (value === 'xl' || value === 'default') return value === 'xl' ? 'large' : 'medium';
  return pickStored(value, TYPE_SIZE_IDS, 'medium');
};

export const readTypeSize = (win = typeof window === 'undefined' ? undefined : window) => {
  try {
    return normalizeTypeSize(win?.localStorage?.getItem(TYPE_SIZE_KEY));
  } catch {
    return 'medium';
  }
};

export const writeTypeSize = (value, win = typeof window === 'undefined' ? undefined : window) => {
  try {
    if (!win?.localStorage) return;
    const next = normalizeTypeSize(value);
    if (next === 'small' || next === 'large') win.localStorage.setItem(TYPE_SIZE_KEY, next);
    else win.localStorage.removeItem(TYPE_SIZE_KEY);
  } catch {
    /* private mode */
  }
};

export const readFont = (win = typeof window === 'undefined' ? undefined : window) => {
  try {
    return pickStored(win?.localStorage?.getItem(FONT_KEY), ['system', 'serif', 'wide'], 'system');
  } catch {
    return 'system';
  }
};

export const writeFont = (value, win = typeof window === 'undefined' ? undefined : window) => {
  try {
    if (!win?.localStorage) return;
    if (value === 'serif' || value === 'wide') win.localStorage.setItem(FONT_KEY, value);
    else win.localStorage.removeItem(FONT_KEY);
  } catch {
    /* private mode */
  }
};

const THEME_EVENT = 'lgl-m-theme-change';

const notifyTheme = () => {
  if (typeof window !== 'undefined') {
    window.dispatchEvent(new Event(THEME_EVENT));
  }
};

export const usePhoneTheme = () => {
  const [preference, setPreference] = useState(() => readThemePreference());
  const [theme, setTheme] = useState(() => resolveTheme(readThemePreference()));
  const [typeSize, setTypeSizeState] = useState(() => readTypeSize());
  const [font, setFontState] = useState(() => readFont());

  useEffect(() => {
    const sync = () => {
      const pref = readThemePreference();
      setPreference(pref);
      setTheme(resolveTheme(pref));
      setTypeSizeState(readTypeSize());
      setFontState(readFont());
    };
    sync();
    window.addEventListener(THEME_EVENT, sync);
    const media = window.matchMedia?.('(prefers-color-scheme: dark)');
    media?.addEventListener?.('change', sync);
    return () => {
      window.removeEventListener(THEME_EVENT, sync);
      media?.removeEventListener?.('change', sync);
    };
  }, []);

  const toggleTheme = () => {
    writeThemePreference(oppositeTheme(resolveTheme(readThemePreference())));
    notifyTheme();
  };

  const setThemePreference = (value) => {
    writeThemePreference(
      value === 'light' || value === 'dark' || value === 'cream' ? value : 'system'
    );
    notifyTheme();
  };

  const setTypeSize = (value) => {
    writeTypeSize(value);
    notifyTheme();
  };

  const setFont = (value) => {
    writeFont(value);
    notifyTheme();
  };

  return {
    preference,
    theme,
    typeSize,
    font,
    toggleTheme,
    setThemePreference,
    setTypeSize,
    setFont,
  };
};
